import { useState, useEffect } from 'react';
import debug from 'debug';
import Auth from '../api/auth';
import { STEP_STATUS, AUTH_ERROR_TYPES } from '../constants/authConstants';
import { TokenValidationError, TOKEN_ERROR_CODES } from '../utils/tokenValidator';
import { serverLogger } from '../libs/datadog-logger/loggers/serverLogger';

// Create debug namespaces
const debugAuth = debug('auth:useTokenAuthentication');
const debugError = debug('auth:useTokenAuthentication:error');

/**
 * Formats a duration in seconds to a human-readable string
 * @param {number} seconds - Duration in seconds
 * @returns {string} Human-readable duration (e.g., "2h 53m 54s")
 */
function formatDuration(seconds) {
  if (typeof seconds !== 'number' || Number.isNaN(seconds) || seconds < 0) return '0s';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
}

/**
 * Custom hook for token-based authentication
 * Provides consistent authentication logic across components
 *
 * @param {Object} params - Authentication parameters
 * @param {string} params.key - Authentication token from URL (primary)
 * @param {string} params.token - Alternative token parameter
 * @returns {Object} Authentication state and helpers
 */
export default function useTokenAuthentication({ key, token } = {}) {
  const [authError, setAuthError] = useState(null);
  const [authErrorType, setAuthErrorType] = useState(null);
  const [authStepOneStatus, setAuthStepOneStatus] = useState(STEP_STATUS.idle);
  const [userName, setUserName] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadStep, setLoadStep] = useState('');

  useEffect(() => {
    const initAuth = () => {
      setAuthError(null);
      setAuthStepOneStatus(STEP_STATUS.inProgress);
      setIsAuthenticating(true);

      try {
        setLoadProgress(15);
        setLoadStep('Authenticating...');

        // Extract token from URL if present
        const urlToken = key || token;

        if (urlToken) {
          // Token provided in URL - ONLY use this token, don't fall back to stored auth
          debugAuth('Token provided in URL, authenticating...');
          let user = null;
          try {
            user = Auth.autoLogin(urlToken);
          } catch (err) {
            debugError('Token auth threw: %O', err);

            // Determine if this is an expired token or an invalid token
            const isExpiredError =
              err instanceof TokenValidationError && err.code === TOKEN_ERROR_CODES.EXPIRED;

            if (isExpiredError) {
              // Token is expired - show the "Link Has Expired" message
              debugError('Token expired - setting EXPIRED_TOKEN error type', {
                metadata: err.metadata,
              });

              // Log to Datadog with detailed expiration info
              const metadata = err.metadata || {};
              const expiredBySeconds = metadata.expiredBySeconds || 0;
              serverLogger.error('auth.token_expired', {
                tokenExpiration: metadata.tokenExpirationISO || 'unknown',
                tokenExpirationUnix: metadata.tokenExpiration || 0,
                currentTime: metadata.currentTimeISO || new Date().toISOString(),
                currentTimeUnix: metadata.currentTime || Math.floor(Date.now() / 1000),
                expiredBySeconds,
                expiredByHuman: formatDuration(expiredBySeconds),
                userId: metadata.userId || 'unknown',
                source: 'useTokenAuthentication',
                errorType: 'expired_token',
              });

              setAuthError(
                'The authentication link has expired. Please request a new link from your representative.',
              );
              setAuthErrorType(AUTH_ERROR_TYPES.EXPIRED_TOKEN);
            } else {
              // Token is invalid (malformed, missing claims, etc.)
              debugError('Token invalid - setting INVALID_TOKEN error type', {
                errorCode: err instanceof TokenValidationError ? err.code : 'unknown',
                field: err instanceof TokenValidationError ? err.field : 'unknown',
              });

              // Log to Datadog for invalid tokens
              serverLogger.error('auth.token_invalid', {
                validationField: err instanceof TokenValidationError ? err.field : 'unknown',
                errorCode: err instanceof TokenValidationError ? err.code : 'UNKNOWN',
                errorMessage: err.message || 'Unknown error',
                source: 'useTokenAuthentication',
                errorType: 'invalid_token',
              });

              setAuthError(
                'The provided authentication token is invalid. Please request a new link.',
              );
              setAuthErrorType(AUTH_ERROR_TYPES.INVALID_TOKEN);
            }

            setIsAuthenticating(false);
            setAuthStepOneStatus(STEP_STATUS.failed);
            return;
          }

          if (user) {
            setUserName(user);
            setIsAuthenticating(false);
            setLoadProgress(25);
            setLoadStep('Authorization successful');
            debugAuth('Authentication successful for user: %s', user);
            setAuthStepOneStatus(STEP_STATUS.success);
          } else {
            // Token provided but invalid - do NOT fall back to existing auth
            debugError('Token provided in URL but authentication failed');
            setAuthError(
              'The provided authentication token is invalid or expired. Please request a new link.',
            );
            setAuthErrorType(AUTH_ERROR_TYPES.EXPIRED_TOKEN);
            setIsAuthenticating(false);
            setAuthStepOneStatus(STEP_STATUS.failed);
          }
        } else {
          // No token in URL - check if we already have valid auth data
          debugAuth('No token in URL, checking for existing authentication...');
          const authData = Auth.getAuthData();
          if (authData && authData.token) {
            // We have existing auth data, extract username
            const user =
              authData.unique_name ||
              authData['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'];
            if (user) {
              setUserName(user);
              setIsAuthenticating(false);
              setLoadProgress(25);
              setLoadStep('Authorization successful');
              debugAuth('Using existing authentication for user: %s', user);
              setAuthStepOneStatus(STEP_STATUS.success);
            } else {
              setAuthError('No valid authentication found');
              setAuthErrorType(AUTH_ERROR_TYPES.MISSING_TOKEN);
              setIsAuthenticating(false);
              setAuthStepOneStatus(STEP_STATUS.failed);
            }
          } else {
            setAuthError(
              'No authentication token provided. Please access this page through the provided link.',
            );
            setAuthErrorType(AUTH_ERROR_TYPES.MISSING_TOKEN);
            setIsAuthenticating(false);
            setAuthStepOneStatus(STEP_STATUS.failed);
          }
        }
      } catch (error) {
        debugError('Authentication error: %O', error);
        setAuthError(error.message || 'Authentication failed');
        setIsAuthenticating(false);
        setAuthStepOneStatus(STEP_STATUS.failed);
      }
    };

    initAuth();
  }, [key, token]);

  /**
   * Helper function to get current auth token from storage
   * @returns {string|null} Authentication token or null if not available
   */
  const getAuthToken = () => {
    const authData = Auth.getAuthData();
    return authData?.token || null;
  };

  return {
    authError,
    authErrorType,
    authStepOneStatus,
    userName,
    isAuthenticating,
    loadProgress,
    loadStep,
    getAuthToken,
  };
}
